<script>
    import { selectedItem, currentUser, 
             analyzeFinancialPlanLoading, analyzeFinancialPlanLoadingDone,
             financialPlanUploadLoading, financialPlanUploadLoadingDone 
           } from '../store/store.js';
    import { getFileName, fetchBrf } from '../utils/utils.js';
    import Logotype from './Logotype.svelte';
    import UploadFile from './UploadFile.svelte'; 
    import Login from './common/Login.svelte'; 
    import { get } from 'svelte/store';

    // Event handlers for the UploadFile component
    function handleFinancialPlanUploadProgress(event) {
        const { stage, loading, done, error, type } = event.detail;
        if (type !== 'financial_plan') return;

        if (stage === 'upload') {
            financialPlanUploadLoading.set(loading);
            financialPlanUploadLoadingDone.set(done && !error);
        } else if (stage === 'analysis') {
            analyzeFinancialPlanLoading.set(loading);
            analyzeFinancialPlanLoadingDone.set(done && !error);
        }
        if (error) {
            console.error(`Error during ${stage} for financial plan:`, event.detail.message);
            // Reset relevant global stores on error if UploadFile doesn't cover it fully
            if (stage === 'upload') { financialPlanUploadLoading.set(false); financialPlanUploadLoadingDone.set(false); }
            if (stage === 'analysis') { analyzeFinancialPlanLoading.set(false); analyzeFinancialPlanLoadingDone.set(false); }
        }
    }

    function handleFinancialPlanUploadComplete(event) {
        const { fileName, coreId, type } = event.detail;
        if (type !== 'financial_plan') return;
        console.log(`Financial plan upload and processing complete for ${fileName}, coreId: ${coreId}`);
        // $selectedItem is updated by fetchBrf called within UploadFile.
        // UI will reactively switch from UploadFile to the analysis/viewer section.
    }

    function handleFinancialPlanUploadError(event) {
        const { message, type, stage } = event.detail;
        if (type !== 'financial_plan') return;
        console.error(`Financial Plan Upload Error from UploadFile component during ${stage}:`, message);
        // Reset all relevant global stores to ensure UI consistency
        financialPlanUploadLoading.set(false);
        financialPlanUploadLoadingDone.set(false);
        analyzeFinancialPlanLoading.set(false);
        analyzeFinancialPlanLoadingDone.set(false);
        // alert(`Upload failed: ${message}`); // Optional user feedback
    }

    async function handleTriggerExistingFinancialPlanAnalysis(){
        if (!$selectedItem?.financial_plan_file_name || !$selectedItem?.core_id) {
            console.warn("Cannot trigger existing financial plan analysis: file name or core ID missing.");
            return;
        }
		try {
			analyzeFinancialPlanLoadingDone.set(false);
			analyzeFinancialPlanLoading.set(true);

            let url = 'https://brf-analyze-backend-tnkskvn76a-lz.a.run.app/analyze_financial_plan';
            const queryParams = new URLSearchParams({ file_name: $selectedItem.financial_plan_file_name });
            if ($selectedItem.core_id) {
                queryParams.append('core_id', $selectedItem.core_id);
            }
            const storeState = get(currentUser); // Helper to get current value
            const response = await fetch(`${url}?${queryParams.toString()}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${storeState?.accessToken}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Failed to trigger backend function for existing financial plan. Status: ${response.status}, Message: ${errorText}`);
                throw new Error('Failed to trigger backend function for existing financial plan');
            }

            const data = await response.json();
            console.log('Backend function response for existing financial plan:', data);
            analyzeFinancialPlanLoadingDone.set(true);
			fetchBrf($selectedItem.core_id); 
		} catch (error) {
			console.error('Error in re-analyzing financial plan process:', error.message);
            analyzeFinancialPlanLoadingDone.set(false);
		} finally {
			analyzeFinancialPlanLoading.set(false);
		}
	}
</script>

{#if !$currentUser?.accessToken && !$selectedItem.financial_plan_file_name}
<div class="key-value-table solo-table" style="max-width: 310px; margin-bottom: 40px;">
  <h3>Boanalys har inte tillgång till den ekonomiska planen</h3>
  <p>Du behöver logga in för att ladda upp den ekonomiska planen.</p>
</div>
<Login />
{:else if !$selectedItem.financial_plan_file_name}
  <!-- Section for uploading financial plan if not available -->
  <div style="margin-top: 0px !important;">
    <UploadFile
        uploadType="financial_plan"
        associatedCoreId={$selectedItem.core_id}
        textsConfig={{ 
            title: `Ladda upp Ekonomisk Plan for ${$selectedItem.name || 'denna förening'}`,
            buttonText: 'Välj Ekonomisk Plan (PDF)',
            infoText: 'Ladda upp den ekonomiska planen för att möjliggöra analys.'
        }}
        on:progress={handleFinancialPlanUploadProgress}
        on:complete={handleFinancialPlanUploadComplete}
        on:error={handleFinancialPlanUploadError}
    />
  </div>

{:else if $selectedItem.financial_plan_file_name}
  <!-- Section for initial analysis state (if file exists but not analyzed, e.g. financial_plan_id is null) -->
  {#if $selectedItem.financial_plan_id === null}
    <div class="analyze-about-text" style="margin-top: 0px !important;">
      {#if !$analyzeFinancialPlanLoading} 
        <div class="ready-state centered-layout">
            <div class="content-container">
                <h5>Analysera den ekonomiska planen för djupare insikter!</h5>
                <p class="description">
                    Den ekonomiska planen finns tillgänglig men är inte analyserad än. Starta analysen för att:
                </p>
                <ul class="benefits-list">
                    <li>Förstå föreningens ekonomiska ställning.</li>
                    <li>Identifiera eventuella avvikelser från standard.</li>
                </ul>
				<p></p>
                <button
                    type="button"
                    class="action-button"
                    on:click={handleTriggerExistingFinancialPlanAnalysis}
                >
                    Starta Analys <span class="button-arrow">&rarr;</span>
                </button>
            </div>
        </div>
      {:else}
        <!-- Show Analysis Loading Card only -->
        <div class="loading-container">
            <h3 class="loading-header">Analyserar ekonomisk plan...</h3>
            <div class="loading-items-wrapper">
                <div class="loading-item">
					<div class="question">
						<Logotype scale={0.6}></Logotype> 
					</div>
                    <div class="loading-text"> <span>Analyserar den ekonomiska planen (ca 20 sek)...</span> </div>
                </div>
            </div>
        </div>
      {/if} 
    </div>
  {/if} 
  
  <!-- PDF Viewer (show if file exists, analysis is done, and user has access) -->
  {#if ($currentUser?.subscription_tier === 'Pro' || $currentUser?.subscription_tier === 'Plus') && $selectedItem.financial_plan_id !== null}
    <div class="pdf-container" style="margin-top: 20px;">
      <iframe class="iframe" title="Ekonomisk Plan" src={ getFileName($selectedItem?.financial_plan_file_name) } frameborder=0 ></iframe>
    </div>
  {/if}
{/if} 

<style>
    p.description {
        font-size: 0.95rem;
        color: #495057;
        line-height: 1.6;
        margin-bottom: 15px;
        text-align: center;
    }
</style>
